import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Send, Clock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const GlobalUserAccessPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [company, setCompany] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null); // null, 'pending', 'rejected'
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publicTheme, setPublicTheme] = useState(null);
  const [countdown, setCountdown] = useState(10);

  // Get company from subdomain
  const getSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[0] !== 'www') {
      return parts[0];
    }
    return null;
  };

  useEffect(() => {
    fetchCompanyAndAccessStatus();
    fetchPublicTheme();
  }, []);

  // Auto-logout countdown when access request is pending
  useEffect(() => {
    if (accessStatus !== 'pending') return;
    if (countdown <= 0) {
      logout();
      // Redirect to main domain so user can log back into their original company
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length >= 2 && parts[0] !== 'www') {
        const mainDomain = parts.slice(1).join('.');
        window.location.href = `${window.location.protocol}//www.${mainDomain}/login`;
      } else {
        window.location.href = '/login';
      }
      return;
    }
    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [accessStatus, countdown, logout]);

  const fetchPublicTheme = async () => {
    try {
      const res = await api.get('/platform-settings/public-theme');
      setPublicTheme(res.data);
    } catch (error) {
      // Use defaults
    }
  };

  const fetchCompanyAndAccessStatus = async () => {
    try {
      const subdomain = getSubdomain();
      if (!subdomain) {
        navigate('/admin/dashboard');
        return;
      }

      // Get company by subdomain
      const companyRes = await api.get(`/companies/by-subdomain/${subdomain}`);
      setCompany(companyRes.data);

      // Check access status
      const accessRes = await api.get(`/company-access/check/${companyRes.data.id}`);
      
      if (accessRes.data.has_access) {
        // User has access, redirect to dashboard
        navigate('/admin/dashboard');
        return;
      }

      setAccessStatus(accessRes.data.status);
    } catch (error) {
      console.error('Error fetching company:', error);
      toast.error('Unable to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/company-access/request', {
        company_id: company.id,
        reason_for_access: reason
      });

      toast.success('Access request submitted successfully');
      setAccessStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = publicTheme?.primaryColor || '#2B4B6A';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      <PublicHeader />
      
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {/* Company Logo/Name */}
            <div className="text-center mb-8">
              {company?.logo_url ? (
                <img 
                  src={resolveFileUrl(company.logo_url, process.env.REACT_APP_BACKEND_URL)}
                  alt={company.name}
                  className="h-16 mx-auto mb-4 object-contain"
                />
              ) : (
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Building2 className="w-8 h-8" style={{ color: primaryColor }} />
                </div>
              )}
              <h1 className="text-2xl font-bold text-gray-900">{company?.name || 'Company'}</h1>
            </div>

            {/* Status Messages */}
            {accessStatus === 'pending' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="text-lg font-semibold text-amber-800 mb-2">Request Pending</h2>
                <p className="text-amber-700 text-sm">
                  Your access request has been submitted and is awaiting approval from {company?.name}.
                  You'll be notified once a decision is made.
                </p>
                <p className="text-amber-600 text-sm mt-3">
                  You will be logged out in {countdown} second{countdown !== 1 ? 's' : ''}...
                </p>
              </div>
            )}

            {accessStatus === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-red-800 mb-2 text-center">Previous Request Declined</h2>
                <p className="text-red-700 text-sm text-center mb-4">
                  Your previous request was declined. You can submit a new request with additional information.
                </p>
              </div>
            )}

            {/* Request Form (show for null or rejected status) */}
            {(accessStatus === null || accessStatus === 'rejected') && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-blue-900 mb-2 text-center">Request Company Access</h2>
                  <p className="text-blue-800 text-sm text-center">
                    You are logged in, but <strong>{company?.name}</strong> needs to authorize your access.
                    Submit a request below and the company admin will review it.
                  </p>
                </div>

                <form onSubmit={handleSubmitRequest} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Access <span className="text-gray-400">(Optional)</span>
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Explain why you'd like access to this company..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows={4}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 text-white py-3 px-6 rounded-xl font-medium transition hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="submit-access-request"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Access Request
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-500 mb-3">
                    Logged in as <strong>{user?.email}</strong>
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => navigate('/admin/dashboard')}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      ← Back to Dashboard
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={logout}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default GlobalUserAccessPage;
